import Toggle from '@ui/Toggle';
import { Bike } from 'models/shared';
import { UserDocument } from 'models/user';
import EditGarageModal from './EditGarageModal';
import MobileEditGarageModal from './MobileEditGarageModal';
import AddBikeModal from '@c/modals/AddGarageModal';
import { AnimatedList, EmptyAnimatedList } from './AnimatedList';
import { isMobile } from '@util/index';

type GarageFilterProps = {
  isMyGarageEnabled: boolean;
  setIsMyGarageEnabled: (enabled: boolean) => void;
  isEditGarageModalOpen: boolean;
  setIsEditGarageModalOpen: (open: boolean) => void;
  isMobileEditGarageModalOpen: boolean;
  setIsMobileEditGarageModalOpen: (open: boolean) => void;
  isAddBikeModalOpen: boolean;
  setIsAddBikeModalOpen: (open: boolean) => void;
  userDoc: UserDocument;
  selectedBikes: Bike[];
  setSelectedBikes: (bikes: Bike[]) => void;
  handleBikeSelection: (args: {
    bike?: Bike | null;
    remove?: boolean;
    bikes?: Bike[];
  }) => void;
  onAddBikeClick: () => void;
  onSyncSelectedBikes: (bikes: Bike[]) => void;
};

export const GarageFilter = ({
  isMyGarageEnabled,
  setIsMyGarageEnabled,
  isEditGarageModalOpen,
  setIsEditGarageModalOpen,
  isAddBikeModalOpen,
  setIsAddBikeModalOpen,
  isMobileEditGarageModalOpen,
  setIsMobileEditGarageModalOpen,
  userDoc,
  selectedBikes,
  setSelectedBikes,
  handleBikeSelection,
  onAddBikeClick,
  onSyncSelectedBikes,
}: GarageFilterProps) => (
  <div className="flex h-fit w-full flex-col items-start gap-6 bg-[#F6F6F6] p-6">
    <div className="flex w-full items-center justify-between">
      <h3 className="text-[1.8rem] font-semibold text-black">My garage</h3>

      <Toggle
        enabled={isMyGarageEnabled}
        onChange={() => setIsMyGarageEnabled(!isMyGarageEnabled)}
      />
    </div>

    <p className="text-[1.6rem] font-medium text-[#7F7F7F]">
      Enable this to only show parts that fit the vehicles in your garage.
    </p>

    <button
      type="button"
      className="bg-transparent text-[1.6rem] font-semibold capitalize text-brand-secondary transition-colors duration-150 hover:text-brand-secondary-darker"
      onClick={() => {
        if (isMobile()) {
          setIsMobileEditGarageModalOpen(true);
        } else {
          setIsEditGarageModalOpen(true);
        }
      }}
    >
      Edit
    </button>

    {userDoc.bikes.length ? (
      <AnimatedList isEnabled={isMyGarageEnabled}>
        {userDoc.bikes?.map((bike: Bike, index: number) => (
          <div
            className="flex w-full items-center justify-between border-t border-[#D9D9D9] pt-4"
            key={`garage-bike-${index}`}
          >
            <div className="flex flex-col items-start">
              <p className="text-[1.6rem] font-semibold text-black">
                {bike.make}
              </p>

              <div className="flex items-center gap-x-1">
                <p className="text-[1.4rem] text-[#7F7F7F]">{bike.year}</p>

                <p className="w-[25rem] truncate text-[1.4rem] text-[#7F7F7F]">
                  {bike.model}
                </p>
              </div>
            </div>

            <Toggle
              enabled={selectedBikes.some(
                (b: Bike) =>
                  b.make === bike.make &&
                  b.model === bike.model &&
                  b.year === bike.year
              )}
              onChange={() => {
                const isSelected = selectedBikes.some(
                  (b: Bike) =>
                    b.make === bike.make &&
                    b.model === bike.model &&
                    b.year === bike.year
                );

                if (isSelected) {
                  const newSelected = selectedBikes.filter(
                    (b: Bike) =>
                      !(
                        b.make === bike.make &&
                        b.model === bike.model &&
                        b.year === bike.year
                      )
                  );
                  setSelectedBikes(newSelected);
                  handleBikeSelection({ bikes: newSelected });
                } else {
                  const newSelected = [...selectedBikes, bike];
                  setSelectedBikes(newSelected);
                  handleBikeSelection({ bikes: newSelected });
                }
              }}
            />
          </div>
        ))}
      </AnimatedList>
    ) : (
      <EmptyAnimatedList isEnabled={isMyGarageEnabled} type="garage" />
    )}

    <EditGarageModal
      isOpen={!isMobile() && isEditGarageModalOpen}
      dismiss={() => setIsEditGarageModalOpen(false)}
      userDoc={userDoc}
      onAddBikeClick={onAddBikeClick}
      onSyncSelectedBikes={onSyncSelectedBikes}
      previousSelectedBikes={selectedBikes}
    />

    <MobileEditGarageModal
      dismiss={() => setIsMobileEditGarageModalOpen(false)}
      onAddBikeClick={onAddBikeClick}
      onSyncSelectedBikes={onSyncSelectedBikes}
      open={isMobile() && isMobileEditGarageModalOpen}
      previousSelectedBikes={selectedBikes}
      userDoc={userDoc}
    />

    <AddBikeModal
      isOpen={isAddBikeModalOpen}
      dismiss={() => setIsAddBikeModalOpen(false)}
    />
  </div>
); 
